import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import graphql from "babel-plugin-relay/macro";
import { AnimalIdentification } from "components/animal/AnimalIdentification";
import { Rabies } from "components/animal/Rabies";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { validationMessages } from "components/shared/validationMessages";
import { Message } from "shared/components/Message";
import { animalMessages } from "domain/animal/animalMessages";
import { UpsertAnimalInput } from "domain/animal/upsertAnimal";
import { useAnimalFieldReset } from "components/animal/useAnimalFieldReset";
import { AnimalBreedsQuery } from "generatedQueries/AnimalBreedsQuery.graphql";
import { Animal_query, Animal_query$key } from "generatedQueries/Animal_query.graphql";
import { Hideable_configuration, Hideable_configuration$key } from "generatedQueries/Hideable_configuration.graphql";
import { LicensingAnimalIdentificationType, LicensingAnimalIdentificationInput } from "generatedQueries/upsertAnimalMutation.graphql";
import { FormatXMLElementFn, PrimitiveType } from "intl-messageformat";
import moment from "moment";
import React, { FC, useEffect } from "react";
import { IntlShape, MessageDescriptor, useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { Col, Row } from "reactstrap";
import Button from "reactstrap/lib/Button";
import { useFormContext, useWatch, useFieldArray } from "react-hook-form";

import { FormLabel } from "shared/components/formInputs/FormLabel";
import { HideableContainer, HideableFields } from "shared/components/Hideable";
import { IRenderProps, Query } from "shared/components/Query";
import { formatIso8601Date, getValidatedDateOrNull } from "shared/dateFunctions";
import { sharedMessages } from "shared/sharedMessages";
import { TranslatedRegisterOptions } from "shared/validations/useTranslatedValidations";

import { TextBox } from "shared/components/newFormInputs/TextBox";
import { FormSelect } from "shared/components/newFormInputs/FormSelect";
import { DatePicker } from "components/shared/datePicker/DatePicker";
import { LabelledCheckbox } from "shared/components/newFormInputs/LabelledCheckbox";
import { RadioGroup } from "shared/components/newFormInputs/RadioGroup";
import { combine, maxLength, required, isGreaterThan, customValidator, isPastDate } from "shared/validations/validations";
import { Radio } from "shared/components/newFormInputs/Radio";

interface IGraphQlProps {
    queryKey: Animal_query$key;
}

interface IProps {
    onAnimalTypeSelected?: (typeId: string) => void;
    titleMessage?: MessageDescriptor;
    titleMessageValues?: { [key: string]: string | PrimitiveType | React.ReactElement | FormatXMLElementFn };
    animalId?: string;
    onDeleteClicked?: (animalId: string) => void;
}

export const Animal: FC<IProps & IGraphQlProps> = ({
    animalId,
    queryKey,
    titleMessage,
    titleMessageValues,
    onDeleteClicked,
}) => {
    const intl = useIntl();
    const query = useFragment(graphQlQuery, queryKey);

    if (!query.account) {
        throw new Error("Must have an account to add or update an animal");
    }

    const { control, register } = useFormContext<UpsertAnimalInput>();

    useEffect(() => {
        register("id");
        register("foundSbAnimalId", { valueAsNumber: true });
    }, [register]);

    useAnimalFieldReset();

    const watchedAnimalTypeId = useWatch({ control, name: "animalTypeId" });
    const watchedDateOfBirth = useWatch({ control, name: "dateOfBirth" });
    const watchedBreedSelection = useWatch({ control, name: "breedSelection" });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "identifications"
    });

    const animalTypeOptions = query.licensingAnimalTypes?.map(t => ({
        label: t.description || "",
        value: t.id,
    })) ?? [];

    const genderOptions = query.genders?.map(g => ({
        label: g.name,
        value: g.genderId,
    })) ?? [];

    return <>
        <Row>
            <Col>
                {onDeleteClicked && !!animalId && (
                    <Button color="danger" className="float-right" onClick={onDeleteButtonClicked}>
                        <Message message={sharedMessages.deleteButtonLabel} />
                    </Button>
                )}
                <h2>
                    <Message message={titleMessage || animalMessages.animalFormLabel} values={titleMessageValues} />
                </h2>
            </Col>
        </Row>

        <FormLabel message={animalMessages.animalNameLabel} required={true} for="name">
            <TextBox<UpsertAnimalInput, "name">
                id="name"
                name="name"
                control={control}
                register={register}
                options={combine<UpsertAnimalInput, "name">(required(), maxLength(50))}
                autoFocus={true}
                aria-required={true}
            />
        </FormLabel>

        <FormLabel message={animalMessages.dateOfBirthLabel} required={true} for="dateOfBirth">
            <DatePicker<UpsertAnimalInput, "dateOfBirth">
                id="dateOfBirth"
                name="dateOfBirth"
                control={control}
                maxDate={moment().add(-1, "days").toDate()}
                rules={combine<UpsertAnimalInput, "dateOfBirth">(required(), isPastDate(intl.formatMessage))}
                aria-required={true}
            />
        </FormLabel>

        <FormLabel message={animalMessages.animalTypeLabel} required={true} for="animalTypeId">
            <FormSelect<UpsertAnimalInput, "animalTypeId">
                name="animalTypeId"
                control={control}
                className="input-animal-type"
                isClearable={false}
                isSearchable={true}
                options={animalTypeOptions}
                rules={combine<UpsertAnimalInput, "animalTypeId">(
                    required(),
                    customValidator({ isValidType: animalTypeValidator(query, intl) })
                )}
                aria-required={true}
            />
        </FormLabel>

        <FormLabel message={animalMessages.genderLabel} required={true} for="genderId">
            <FormSelect<UpsertAnimalInput, "genderId">
                name="genderId"
                control={control}
                className="input-animal-gender"
                isClearable={false}
                isSearchable={true}
                options={genderOptions}
                rules={combine<UpsertAnimalInput, "genderId">(
                    required(),
                    isGreaterThan(intl.formatMessage, 0, validationMessages.requiredError)
                )}
                aria-required={true}
            />
        </FormLabel>

        <LabelledCheckbox<UpsertAnimalInput, "isDesexed">
            id="isDesexed"
            name="isDesexed"
            control={control}
            register={register}
            message={animalMessages.isDesexedLabel}
        />

        {watchedAnimalTypeId && (
            <Query<AnimalBreedsQuery>
                query={graphql`
                    query AnimalBreedsQuery($animalTypeId: ID!, $dateOfBirth: LocalDate) {
                        breeds: licensingBreeds(animalTypeId: $animalTypeId, dateOfBirth: $dateOfBirth) {
                            breedId
                            name
                        }
                        licensingAnimalColours(animalTypeId: $animalTypeId, dateOfBirth: $dateOfBirth) {
                            colourId
                            name
                        }
                    }`}
                variables={{
                    animalTypeId: watchedAnimalTypeId,
                    dateOfBirth: getDateOfBirthOrNull(watchedDateOfBirth),
                }}>
                {renderAnimalTypeSpecificSection}
            </Query>
        )}

        <FormLabel message={animalMessages.identificationLabel}>
            {!fields.length && (
                <span className="d-block mb-3">
                    <Message message={animalMessages.noIdentificationsMessage} />
                </span>
            )}

            {fields.map((field, i) => {
                const typePath = `identifications.${i}.type` as const;
                const valuePath = `identifications.${i}.value` as const;
                return (
                    <AnimalIdentification
                        key={field.id}
                        index={i}
                        control={control}
                        register={register}
                        onRemoveClicked={() => remove(i)}
                        typeRules={required<UpsertAnimalInput, typeof typePath>()}
                        valueRules={combine<UpsertAnimalInput, typeof valuePath>(
                            required<UpsertAnimalInput, typeof valuePath>(),
                            maxLength<UpsertAnimalInput, typeof valuePath>(50),
                            customValidator({ isIdentValid: validateIdentificationValue(i, query.configuration, intl) })
                        ) as TranslatedRegisterOptions<UpsertAnimalInput, `identifications.${number}.value`>}
                    />
                );
            })}

            <Button className="mt-2" color="success" onClick={() => append({ type: "MICROCHIP", value: "" })} aria-label="Add Identification" size="sm">
                <FontAwesomeIcon icon={faPlus} className="icon-button mr-1" />
                <Message message={animalMessages.addIdentificationLabel} />
            </Button>
        </FormLabel>

        <HideableContainer
            field={HideableFields.rabiesVaccination}
            configuration={query.configuration as Hideable_configuration | null}
            >
            <Rabies
                configurationKey={query.configuration}
                accountKey={query.account}
            />
        </HideableContainer>
    </>;

    function renderAnimalTypeSpecificSection({ error, props: animalTypeSpecificRenderProps }: IRenderProps<AnimalBreedsQuery>) {
        if (error) {
            return <ErrorMessage message={sharedMessages.requestFailedBody} />;
        }

        const breedOptions = animalTypeSpecificRenderProps?.breeds?.map(b => ({ label: b.name, value: b.breedId })) ?? [];
        const colourOptions = animalTypeSpecificRenderProps?.licensingAnimalColours?.map(c => ({ label: c.name, value: c.colourId })) ?? [];

        return <>
            <FormLabel message={animalMessages.breedLabel} required={true} for="breedId">
                <FormSelect<UpsertAnimalInput, "breedId">
                    name="breedId"
                    control={control}
                    className="input-animal-breed"
                    isClearable={false}
                    isSearchable={true}
                    isLoading={!animalTypeSpecificRenderProps}
                    options={breedOptions}
                    rules={combine<UpsertAnimalInput, "breedId">(required(), isGreaterThan(intl.formatMessage, 0, validationMessages.requiredError))}
                    aria-required={true}
                />
            </FormLabel>

            <RadioGroup<UpsertAnimalInput, "breedSelection">
                control={control}
                register={register}
                name="breedSelection"
                label={animalMessages.breedSelectionLabel}
                isRequired={true}>
                    {{
                        radios: (registerRadio) => <>
                            <Radio {...registerRadio()} label={animalMessages.purebredLabel} value="PUREBRED" />
                            <Radio {...registerRadio()} label={animalMessages.mixedBreedLabel} value="MIXED" />
                        </>,
                    }}
            </RadioGroup>

            <FormLabel message={animalMessages.secondaryBreedLabel} id="secondaryBreedLabel" for="secondaryBreedId">
                <FormSelect<UpsertAnimalInput, "secondaryBreedId">
                    name="secondaryBreedId"
                    control={control}
                    className="input-animal-secondary-breed"
                    isClearable={true}
                    isSearchable={true}
                    isLoading={!animalTypeSpecificRenderProps}
                    isDisabled={watchedBreedSelection !== "MIXED"}
                    options={breedOptions}
                    aria-labelledby="secondaryBreedLabel secondaryBreedDescription"
                />
                <small className="form-text text-muted" id="secondaryBreedDescription">
                    <Message message={animalMessages.secondaryBreedHelpMessage} />
                </small>
            </FormLabel>

            <FormLabel message={animalMessages.colourLabel} required={true} for="colourId">
                <FormSelect<UpsertAnimalInput, "colourId">
                    name="colourId"
                    control={control}
                    className="input-animal-colour"
                    isClearable={false}
                    isSearchable={true}
                    isLoading={!animalTypeSpecificRenderProps}
                    options={colourOptions}
                    rules={combine<UpsertAnimalInput, "colourId">(required(), isGreaterThan(intl.formatMessage, 0, validationMessages.requiredError))}
                    aria-required={true}
                />
            </FormLabel>

            <FormLabel message={animalMessages.secondaryColourLabel} for="secondaryColourId">
                <FormSelect<UpsertAnimalInput, "secondaryColourId">
                    name="secondaryColourId"
                    control={control}
                    className="input-animal-secondary-colour"
                    isClearable={true}
                    isSearchable={true}
                    isLoading={!animalTypeSpecificRenderProps}
                    options={colourOptions}
                />
            </FormLabel>
        </>;
    }

    function onDeleteButtonClicked() {
        if (onDeleteClicked && animalId) {
            onDeleteClicked(animalId);
        }
    }
};

export function validateIdentificationValue(index: number, configuration: Animal_query["configuration"], intl: IntlShape) {
    return (value: string | undefined, formValues: UpsertAnimalInput) => {
        const ident = formValues.identifications?.[index];
        if (!ident || !ident.type) {
            return undefined;
        }

        const countErrors = checkIdentificationCount(ident.type, index, formValues.identifications ?? [], intl);
        if (countErrors) {
            return countErrors;
        }

        if (ident.type === "MICROCHIP" && value && configuration?.microchipValidationSettings) {
            const { minChipLength, microchipLength } = configuration.microchipValidationSettings;
            const len = value.length;

            if (minChipLength > 0) {
                if (len !== minChipLength && len !== microchipLength) {
                    return intl.formatMessage(animalMessages.microchipNumberInvalidDualLengthError, { minLength: minChipLength, length: microchipLength });
                }
            } else if (microchipLength > 0 && len !== microchipLength) {
                return intl.formatMessage(animalMessages.microchipNumberInvalidSingleLengthError, { length: microchipLength });
            }
        }
        return undefined;
    };
}

function checkIdentificationCount(type: LicensingAnimalIdentificationType | string, currentIndex: number, identifications: LicensingAnimalIdentificationInput[], intl: IntlShape) {
    let maxCount = 0;
    let invalidMessage: MessageDescriptor = animalMessages.identificationCountInvalidError;

    switch (type) {
        case "MICROCHIP": maxCount = 2; invalidMessage = animalMessages.microchipIdentificationCountInvalidError; break;
        case "LICENCE": maxCount = 1; invalidMessage = animalMessages.licenceIdentificationCountInvalidError; break;
        case "TATTOO": maxCount = 1; invalidMessage = animalMessages.tattooIdentificationCountInvalidError; break;
    }

    const currentTypeCount = identifications.filter(i => i && i.type === type).length;

    if (currentTypeCount > maxCount) {
        const typedIndices = identifications.map((ident, i) => ident?.type === type ? i : -1).filter(i => i !== -1);
        if (typedIndices.indexOf(currentIndex) >= maxCount) {
            return intl.formatMessage(invalidMessage, { maxCount });
        }
    }
    return undefined;
}

export function animalTypeValidator(renderForm: Animal_query, intl: IntlShape) {
    return (animalTypeId: string | undefined) => {
        if (!animalTypeId) {
            return undefined;
        }
        const animalType = renderForm.licensingAnimalTypes?.find(type => type.id === animalTypeId);
        return !animalType ? intl.formatMessage(animalMessages.invalidAnimalTypeError) : undefined;
    };
}

export function getDateOfBirthOrNull(dateOfBirth: string | undefined | null): string | null {
    if (!dateOfBirth) {
        return null;
    }
    const validatedDateOfBirth = getValidatedDateOrNull(dateOfBirth);

    return validatedDateOfBirth ? formatIso8601Date(validatedDateOfBirth) : null;
}

const graphQlQuery = graphql`
    fragment Animal_query on Query {
        genders: licensingGenders {
            genderId
            name
        }
        licensingAnimalTypes {
            id
            description
        }
        configuration {
            microchipValidationSettings {
                microchipLength
                minChipLength
            }
            ...Rabies_configuration
            ...Hideable_configuration
        }
        account {
            ...Rabies_account
        }
    }`;
